import _get from 'lodash/get';
import { factory as splitFactory, store } from 'src/main';

const SPLIT_IO_TREATMENT = 'enterprise_v2_auth';
const AUTH_VERSION_COOKIE = 'sl-auth-version';
const LEGACY_AUTH_METHOD_KEY = 'sl-auth-method';

// Local testing override: set to true/false to bypass Split.io, or null to use Split.io
const LOCAL_OVERRIDE: boolean | null = null;

// ============================================================================
// Auth-version source of truth
// ----------------------------------------------------------------------------
// The API sets a non-HttpOnly `sl-auth-version=v1|v2` cookie at
// Domain=.stackline.com in the same response that issues the v1 or v2 session
// cookies (see Stackline.Api/Authentication/AuthenticationHelper.RenewAuthCookie
// and Stackline.Api/Controllers/Auth/AuthV2Controller.SetAuthCookie). It is
// cleared on logout. JS reads it synchronously at any *.stackline.com origin.
//
// This replaces the previous localStorage-based `sl-auth-method` flag, which
// was per-origin (atlas vs enterprise-sso did not share storage) and survived
// browser cookie clears, producing stale-state bugs when the user signed in as
// a different version than their last session.
//
// LEGACY_AUTH_METHOD_KEY is read only as a transition-period fallback for
// sessions issued before the API marker shipped. It is no longer written.
// Safe to delete this fallback once all live sessions have rolled.
// ============================================================================

function readAuthVersionCookie(): 'v1' | 'v2' | null {
  try {
    const match = document.cookie.match(/(?:^|;\s*)sl-auth-version=(v1|v2)/);
    return match ? (match[1] as 'v1' | 'v2') : null;
  } catch {
    return null;
  }
}

function readLegacyAuthMethod(): 'v1' | 'v2' | null {
  try {
    const value = localStorage.getItem(LEGACY_AUTH_METHOD_KEY);
    return value === 'v1' || value === 'v2' ? value : null;
  } catch {
    return null;
  }
}

/**
 * Get the auth version of the current session, or null if not signed in.
 *
 * Source order:
 *  1. `sl-auth-version` cookie (server-set, authoritative)
 *  2. Legacy `sl-auth-method` localStorage (transition fallback)
 */
export function getAuthVersion(): 'v1' | 'v2' | null {
  return readAuthVersionCookie() ?? readLegacyAuthMethod();
}

/**
 * Evaluate the Split.io treatment for a specific email.
 *
 * Used **pre-sign-in only** — on enterprise-sso after the user enters their
 * email in the sign-in form, to decide whether to route them through the v1
 * or v2 sign-in flow. Post-sign-in, the auth version is determined by the
 * marker cookie (see `isV2Enabled`), not by Split.
 */
export function evaluateV2ForEmail(email: string): boolean {
  if (LOCAL_OVERRIDE !== null) return LOCAL_OVERRIDE;

  const splitClient = splitFactory.client();
  const urlParams = new URLSearchParams(window.location.search);

  return (
    splitClient.getTreatment(SPLIT_IO_TREATMENT, {
      host: window.location.hostname,
      referrer: urlParams.get('referrer') || '',
      email,
    }) === 'on'
  );
}

/**
 * Determine whether to use the V2 auth shell on app hosts.
 *
 * Synchronously reads the `sl-auth-version` cookie set by the API on session
 * issue. Falls back to the legacy localStorage flag for in-flight sessions
 * issued before the marker cookie shipped, then to a Split.io evaluation
 * (only effective if Redux happens to have the email already, which is
 * unusual at AppSwitch mount).
 */
export function isV2Enabled(): boolean {
  if (LOCAL_OVERRIDE !== null) return LOCAL_OVERRIDE;

  const version = getAuthVersion();
  if (version === 'v2') return true;
  if (version === 'v1') return false;

  // No marker and no legacy flag -- no session, or a brand-new tab before any
  // sign-in. The Split fallback below is rarely reachable in practice (Redux
  // email is empty at first paint) and is kept for parity with the previous
  // behavior; the typical path is "no version -> default V1 -> AuthShell
  // bounces to enterprise-sso".
  const userEmail = _get(store.getState(), ['user', 'session', 'email'], '');
  if (!userEmail) {
    return false;
  }

  const splitClient = splitFactory.client();
  const urlParams = new URLSearchParams(window.location.search);
  const divisionId = _get(store.getState(), ['user', 'session', 'divisionId'], '');
  const beaconClientId = _get(store.getState(), ['user', 'config', 'vendor', 'BeaconClientId'], '');

  return (
    splitClient.getTreatment(SPLIT_IO_TREATMENT, {
      host: window.location.hostname,
      referrer: urlParams.get('referrer') || '',
      email: userEmail,
      divisionId,
      beaconClientId
    }) === 'on'
  );
}
